<template>
  <v-card class="mt-4">
    <v-snackbar v-model="snackbar" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
    <v-card-text>
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedTenant"
            :items="tenants"
            item-text="tenantName"
            item-value="tenantId"
            label="Select Tenant"
            outlined
            dense
            clearable
            @change="onTenantChange"
          />
        </v-col>
        <v-col v-if="selectedTenantName" cols="12" md="auto" class="d-flex align-center">
          <v-chip color="primary" small>{{ selectedTenantName }}</v-chip>
        </v-col>
      </v-row>
      <v-data-table v-if="selectedTenant" :search="search" :loading="loading" :headers="headers" :items="items">
        <template v-slot:top>
          <v-row>
            <v-spacer />
            <v-col cols="2">
              <v-text-field v-model="search" label="Search" solo dense prepend-inner-icon="mdi-magnify" />
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" :disabled="!selectedTenant" @click="openAddDialog">Add Building</v-btn>
            </v-col>
          </v-row>
        </template>
        <template v-slot:item.enabled="{ item }">
          <v-icon :color="item.enabled ? 'green' : 'grey'">{{ item.enabled ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          <v-btn x-small text @click="toggleEnabled(item)">{{ item.enabled ? 'Disable' : 'Enable' }}</v-btn>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn x-small text @click="openEditDialog(item)">Edit</v-btn>
        </template>
      </v-data-table>
      <v-alert v-else type="info" class="mt-4">Select a tenant to manage buildings</v-alert>
      <v-dialog v-model="dialog" max-width="500" persistent>
        <v-card>
          <v-card-title>{{ isEdit ? 'Edit Building' : 'Add Building' }}</v-card-title>
          <v-card-text>
            <v-text-field v-if="isEdit" v-model="form.BuildingId" label="Building ID" outlined dense disabled />
            <v-text-field v-model="form.BuildingName" label="Building Name" outlined dense />
            <v-text-field
              v-model="form.BuildingImage"
              label="Cover Photo URL"
              placeholder="https://..."
              outlined
              dense
              hint="รูปภาพปกสำหรับหน้า Overview"
              persistent-hint
            />
            <v-img
              v-if="form.BuildingImage"
              :src="form.BuildingImage"
              max-height="120"
              contain
              class="mt-2 rounded"
              @error="coverImageError = true"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5" />
                </v-row>
              </template>
            </v-img>
            <v-alert v-if="coverImageError" type="warning" dense class="mt-2" text>
              โหลดรูปไม่สำเร็จ กรุณาตรวจสอบ URL
            </v-alert>
            <v-checkbox v-model="form.enabled" label="Enabled" hide-details class="mt-2" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="posting" @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { getAdminBuildings, postAdminBuilding, patchAdminBuilding } from '@/api/'

export default {
  name: 'AdminBuildingTable',
  props: {
    tenants: { type: Array, default: () => [] }
  },
  data() {
    return {
      search: '',
      loading: false,
      posting: false,
      snackbar: false,
      snackbarMessage: '',
      dialog: false,
      isEdit: false,
      selectedTenant: null,
      items: [],
      coverImageError: false,
      form: {
        BuildingId: '',
        BuildingName: '',
        BuildingImage: '',
        enabled: true
      },
      headers: [
        { text: 'Tenant Name', value: 'tenantName' },
        { text: 'Building ID', value: 'BuildingId' },
        { text: 'Building Name', value: 'BuildingName' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    selectedTenantName() {
      if (!this.selectedTenant) return ''
      const t = this.tenants.find((x) => x.tenantId === this.selectedTenant)
      return t ? (t.tenantName || t.tenantId) : ''
    }
  },
  watch: {
    'form.BuildingImage'() {
      this.coverImageError = false
    },
    tenants() {
      if (this.selectedTenant && !this.tenants.find((t) => t.tenantId === this.selectedTenant)) {
        this.selectedTenant = null
        this.items = []
      }
    }
  },
  methods: {
    onTenantChange() {
      if (this.selectedTenant) {
        this.load()
        this.$emit('refresh-buildings', this.selectedTenant)
      } else {
        this.items = []
      }
    },
    load() {
      if (!this.selectedTenant) return
      this.loading = true
      getAdminBuildings(
        this.selectedTenant,
        (res) => {
          const tenantName = this.selectedTenantName || ''
          this.items = (res.data || []).map((b) => ({ ...b, tenantName }))
          this.loading = false
        },
        () => {
          this.loading = false
          this.showMessage('Failed to load buildings')
        }
      )
    },
    openAddDialog() {
      this.isEdit = false
      this.coverImageError = false
      this.form = { BuildingId: '', BuildingName: '', BuildingImage: '', enabled: true }
      this.dialog = true
    },
    openEditDialog(item) {
      this.isEdit = true
      this.coverImageError = false
      this.form = {
        tenantId: this.selectedTenant,
        BuildingId: item.BuildingId,
        BuildingName: item.BuildingName || '',
        BuildingImage: item.BuildingImage || '',
        enabled: item.enabled !== false
      }
      this.dialog = true
    },
    save() {
      this.posting = true
      const payload = {
        tenantId: this.selectedTenant,
        BuildingName: this.form.BuildingName,
        BuildingImage: this.form.BuildingImage || undefined,
        enabled: this.form.enabled
      }
      if (this.isEdit) {
        payload.BuildingId = this.form.BuildingId
        patchAdminBuilding(
          payload,
          () => {
            this.posting = false
            this.dialog = false
            this.showMessage('Building updated')
            this.load()
          },
          (err) => {
            this.posting = false
            this.showMessage(err?.response?.data?.error || 'Failed to update')
          }
        )
      } else {
        postAdminBuilding(
          payload,
          () => {
            this.posting = false
            this.dialog = false
            this.showMessage('Building created')
            this.load()
          },
          (err) => {
            this.posting = false
            this.showMessage(err?.response?.data?.error || 'Failed to create')
          }
        )
      }
    },
    toggleEnabled(item) {
      patchAdminBuilding(
        { tenantId: this.selectedTenant, BuildingId: item.BuildingId, enabled: !item.enabled },
        () => {
          this.showMessage('Updated')
          this.load()
        },
        (err) => this.showMessage(err?.response?.data?.error || 'Failed')
      )
    },
    showMessage(msg) {
      this.snackbarMessage = msg
      this.snackbar = true
    }
  }
}
</script>
